import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, ApiError, NetworkError } from '../lib/api';

type Phase = 'verifying' | 'error';

/**
 * Aterrizaje del enlace mágico: /auth/callback?token=<token>.
 * Canjea el token por la cookie de sesión y navega a la lista de tenants.
 *
 * Este path es un contrato con el backend: el correo del enlace mágico
 * debe apuntar a <SPA_ORIGIN>/auth/callback?token=...
 */
export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('verifying');
  const [error, setError] = useState<string>('');
  // StrictMode monta el efecto dos veces en dev; el token es single-use,
  // así que protegemos con un ref para no canjearlo dos veces.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = params.get('token');
    if (!token) {
      setError('El enlace no es válido: falta el token de acceso.');
      setPhase('error');
      return;
    }

    api
      .post('/api/super-admin/auth/login', { token })
      .then(() => {
        navigate('/tenants', { replace: true });
      })
      .catch((err: unknown) => {
        if (err instanceof NetworkError) {
          setError('No se pudo conectar con el servidor. Intenta de nuevo.');
        } else if (err instanceof ApiError) {
          // 400/401 típicamente: token vencido o ya usado.
          setError(
            err.status === 401 || err.status === 400
              ? 'El enlace venció o ya fue usado. Solicita uno nuevo.'
              : err.message,
          );
        } else {
          setError('Ocurrió un error inesperado al verificar el enlace.');
        }
        setPhase('error');
      });
  }, [params, navigate]);

  return (
    <div className="center-wrap">
      <div className="card card-narrow">
        <div className="eyebrow">Inventario · Super Admin</div>
        {phase === 'verifying' ? (
          <>
            <h1 className="page-title mt-8">Verificando acceso…</h1>
            <p className="lead mt-8">Un momento, estamos validando tu enlace.</p>
          </>
        ) : (
          <>
            <h1 className="page-title mt-8">No pudimos iniciar sesión</h1>
            <div className="msg msg-error mt-16">{error}</div>
            <Link to="/login" className="btn btn-primary btn-block mt-16">
              Volver al inicio de sesión
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
